import React, { createContext, useContext, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../hooks/storeHooks";
import { removeAuthTokens } from "../helpers/authHelpers";
import { logoutUserThunk } from "../store/user/userThunk";
import { ROUTE_PATHS } from "../routes/index";
import { resetPhotoLibraryState } from "../store/b2b/photoLibrary/photoLibrarySlice";
import { logoutUser } from "../store/user/userSlice";
import { resetTerms } from "../store/terms/termsSlice";

interface iUserContextProp {
	logout: (redirection: string, callback?: () => void) => void;
}

export const UserContext = createContext<iUserContextProp | null>(null);

export const useUserContext = (): iUserContextProp => {
	const context = useContext(UserContext);
	if (!context) {
		throw new Error("useToast must be used within a ToastContextProvider");
	}
	return context;
};

interface iUserContextProviderProps {
	children: ReactNode;
}

const UserContextProvider: React.FC<iUserContextProviderProps> = ({ children }) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	// @TODO Manage callback for logout.
	const handleLogout = (redirection: string, callback?: () => void) => {
		dispatch(logoutUserThunk({})).then(() => {
			dispatch(logoutUser());
			// Otherwise a stale `viewerMode` (or a stale `current` from the previous
			// account) survives the logout and the next login — in the same tab,
			// with no page reload to clear it — renders the blocking viewer
			// immediately, before the new session has fetched anything.
			dispatch(resetTerms());
			if (!redirection) {
				navigate(redirection);
			}
			navigate(ROUTE_PATHS.LOGIN);
			callback?.();
		});
	};

	return <UserContext.Provider value={{ logout: handleLogout }}>{children}</UserContext.Provider>;
};

export default UserContextProvider;
